import type {
  ApiResponse,
  AuthUser,
  AvailableSlot,
  AvailabilityNextResponse,
  Booking,
  BookingUpdateInput,
  BookingUpdateResult,
  BookingExtraSelection,
  BookingParticipant,
  BookingParticipantInput,
  CatalogPosition,
  CatalogPositionsInput,
  BookingRequest,
  BookingCheckout,
  CouponRedemption,
  CartCheckout,
  CartItem,
  CustomerInput,
  CalendarResponse,
  CustomerSummary,
  DashboardOverview,
  DashboardOverviewQuery,
  DashboardSummary,
  MobileBootstrap,
  ReportBookingPoint,
  ReportOverview,
  ReportRevenuePoint,
  EventSummary,
  EntityMedia,
  EntityMediaType,
  EventPeriod,
  EventCheckout,
  EventCheckoutRequest,
  EventQuoteRequest,
  ServiceQuoteRequest,
  HealthResponse,
  IntegrationAccountBinding,
  IntegrationCatalogEntry,
  IntegrationSummary,
  InvoiceSummary,
  BookingDeposit,
  PaymentLink,
  NotificationChannel,
  NotificationCatalog,
  NotificationDefinition,
  NotificationDefinitionInput,
  NotificationDefinitionUpdate,
  NotificationPreferences,
  NotificationTemplate,
  NotificationTemplateUpdate,
  NotificationSummary,
  PaymentSummary,
  PaymentCheckout,
  PaymentLinkStatusResponse,
  PaymentProviderEnvironment,
  PaymentProviderName,
  PaymentTransactionSummary,
  PriceQuote,
  PricingRule,
  PricingRuleInput,
  PricingRulePatch,
  RefundSummary,
  RecurringFrequency,
  RecurringRule,
  RecurringSeriesCheckout,
  RecurringSeriesCheckoutInput,
  RecurringSeriesPreview,
  RecurringSeriesPreviewInput,
  RecurringSeries,
  ServiceRecurrenceSettings,
  ServicePaymentMode,
  ShoppingCart,
  SignedAction,
  ServiceSummary,
  TaxDefinition,
  TaxInput,
  TaxPatch,
  CustomFieldDefinition,
  CustomFieldInput,
  CustomFieldPatch,
  CustomFieldValue,
  CustomFieldValueInput,
  ProviderDetail,
  ProviderInput,
  ProviderSummary,
  ProviderException,
  ProviderServiceAssignment,
  WeeklySchedule,
  SlotHoldRequest,
  SlotHoldResult,
  StaffBookingRequest,
  ApiKeyCreated,
  ApiKeySummary,
  ApiKeyScope,
  BackupRun,
  CredentialVersion,
  DeviceSummary,
  MfaCredential,
  MfaEnrollment,
  MfaMethod,
  WebAuthnPublicKeyOptions,
  SecurityEvent,
  SsoConnection,
  ScimUser,
  ScimGroup,
  SupportAccessGrant,
  SupportAccessSession,
  WebhookEndpoint,
  PasswordChangeResult,
  AdminPasswordRecoveryResult,
  ConciergeConfigPatch,
  ConciergeConfigResponse
} from "@kuroo/contracts";

export type { AdminPasswordRecoveryResult, ApiKeyCreated, ApiKeySummary, ApiKeyScope, AvailabilityNextResponse, BackupRun, CatalogPosition, CatalogPositionsInput, ConciergeConfigPatch, ConciergeConfigResponse, CredentialVersion, CustomerInput, CustomFieldDefinition, CustomFieldInput, CustomFieldPatch, CustomFieldValue, CustomFieldValueInput, DeviceSummary, EntityMedia, EntityMediaType, IntegrationAccountBinding, MfaCredential, MfaEnrollment, MfaMethod, NotificationCatalog, NotificationDefinition, NotificationDefinitionInput, NotificationDefinitionUpdate, NotificationTemplateUpdate, PasswordChangeResult, ProviderDetail, ProviderException, ProviderInput, ProviderServiceAssignment, ProviderSummary, SecurityEvent, SsoConnection, ScimUser, ScimGroup, SupportAccessGrant, SupportAccessSession, TaxDefinition, TaxInput, TaxPatch, WebhookEndpoint, WebAuthnPublicKeyOptions, WeeklySchedule } from "@kuroo/contracts";
export type { BookingAdjustment, BookingExtraSelection, BookingCheckout, BookingUpdateInput, BookingUpdateResult, StaffBookingRequest, EventCheckout, EventCheckoutRequest, EventQuoteRequest, ServiceQuoteRequest, PriceQuote, PriceQuoteBreakdown, CouponRedemption, PricingRule, PricingRuleInput, PricingRulePatch } from "@kuroo/contracts";

export type { paths as KurooApiPaths } from "./generated-api";

export interface AdminExportRequest {
  format?: "json" | "csv" | "zip";
  scope?: { entities?: string[] };
}

export interface AdminExport {
  id: string;
  format: "json" | "csv" | "zip";
  scope: { entities?: string[] };
  status: "queued" | "processing" | "completed" | "failed";
  file_id?: string | null;
  r2_key?: string | null;
  error?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface AdminImport {
  id: string;
  file_id: string;
  status: "queued" | "processing" | "completed" | "failed";
  summary?: unknown;
  error?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface CatalogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  status: "draft" | "visible" | "archived";
  sort_order?: number;
}

export interface CatalogExtra {
  id: string;
  service_id: string;
  name: string;
  description?: string | null;
  price_cents: number;
  duration_minutes: number;
  max_quantity: number;
  status: "active" | "inactive";
}

export interface CatalogPackage {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  price_cents: number;
  currency: string;
  capacity: number;
  status: "draft" | "visible" | "archived";
}

export interface CatalogLocation {
  id: string;
  name: string;
  address?: string | null;
  timezone: string;
  status: "active" | "inactive";
}

export interface LocationInput {
  name: string;
  address?: string | null;
  timezone?: string;
  status?: CatalogLocation["status"];
}

export type LocationPatch = Partial<LocationInput>;

export interface CatalogProvider {
  id: string;
  display_name: string;
  bio?: string | null;
  color?: string | null;
  photoUrl?: string | null;
  status: "active" | "inactive";
}

export interface CatalogResource {
  id: string;
  location_id?: string | null;
  name: string;
  type: string;
  capacity: number;
  status: "active" | "inactive";
}

export interface AvailabilityRule {
  id: string;
  provider_id?: string | null;
  location_id?: string | null;
  weekday: number;
  start_time: string;
  end_time: string;
  timezone: string;
  valid_from?: string | null;
  valid_until?: string | null;
}

export interface AvailabilityException {
  id: string;
  provider_id?: string | null;
  location_id?: string | null;
  starts_at: string;
  ends_at: string;
  kind: "blocked" | "open";
  reason?: string | null;
}

export interface WaitlistEntry {
  id: string;
  service_id?: string | null;
  event_period_id?: string | null;
  customer_id?: string | null;
  email: string;
  persons: number;
  status: "waiting" | "offered" | "accepted" | "expired" | "cancelled";
}

export interface EventTicket {
  id: string;
  event_id: string;
  name: string;
  description?: string | null;
  price_cents: number;
  capacity: number;
  status: "active" | "inactive";
}

export interface EventAttendee {
  id: string;
  period_id: string;
  ticket_id?: string | null;
  booking_id?: string | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  status: "registered" | "checked_in" | "cancelled";
  hold_id?: string | null;
}

export interface CouponTarget {
  entityType: "service" | "event" | "package";
  entityId: string;
}

export interface Coupon {
  id: string;
  code: string;
  kind: "percentage" | "fixed";
  amount: number;
  currency?: string | null;
  max_redemptions?: number | null;
  redeemed_count: number;
  valid_from?: string | null;
  valid_until?: string | null;
  created_at?: string;
  updated_at?: string;
  status: "active" | "inactive" | "expired";
  targets?: CouponTarget[];
}

export type CouponDetails = Coupon;

export interface TeamUser extends AuthUser {
  phone?: string | null;
  timezone?: string;
  locale?: string;
  created_at?: string;
  updated_at?: string;
}

export interface KurooClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  credentials?: RequestCredentials;
}

export interface RecurringSeriesInput {
  serviceId: string;
  providerId?: string | null;
  locationId?: string | null;
  frequency: RecurringFrequency;
  interval?: number;
  startsAt: string;
  endsAt?: string | null;
  count?: number;
  timezone?: string;
  rule?: RecurringRule;
}

export interface RecurringBookingOccurrence {
  holdId: string;
  slotKey: string;
  startAt: string;
  endAt: string;
  providerId?: string | null;
  locationId?: string | null;
}

export interface MfaLoginChallenge {
  mfaRequired: true;
  challengeId: string;
  challenge: string;
  methods: MfaMethod[];
  expiresAt?: string;
  /** Present when at least one registered passkey can satisfy the challenge. */
  publicKey?: WebAuthnPublicKeyOptions;
}

export type LoginResult = AuthUser | MfaLoginChallenge;

export interface SessionSummary {
  id: string;
  authMethod: "password" | "sso" | "api-key" | "support";
  mfaVerified: boolean;
  userAgent?: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  deviceId?: string | null;
  current: boolean;
}

export interface ScimTokenSummary {
  id: string;
  name: string;
  scopes: string[];
  status: "active" | "revoked";
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  revokedAt?: string | null;
}

export interface ScimTokenCreated extends ScimTokenSummary {
  token: string;
}

/** RFC 7644 list response returned by the SCIM surface. */
export interface ScimListResponse<T> {
  schemas: string[];
  totalResults: number;
  startIndex: number;
  itemsPerPage: number;
  Resources: T[];
}

export interface ScimPatchOperation {
  op: "add" | "replace" | "remove";
  path?: string;
  value?: unknown;
}

export interface ScimUserInput {
  schemas?: string[];
  externalId?: string | null;
  userName: string;
  active?: boolean;
  name?: { givenName?: string; familyName?: string };
  roles?: Array<{ value: string }>;
}

export interface ScimGroupInput {
  schemas?: string[];
  externalId?: string | null;
  displayName: string;
  members?: Array<{ value: string; type?: string }>;
  role?: string;
}

export interface ScimServiceProviderConfig {
  schemas: string[];
  patch: { supported: boolean };
  bulk: { supported: boolean; maxOperations: number; maxPayloadSize: number };
  filter: { supported: boolean; maxResults: number };
  changePassword: { supported: boolean };
  sort: { supported: boolean };
  etag: { supported: boolean };
  authenticationSchemes: Array<{ type: string; name: string; description?: string }>;
}

export interface WebhookDeliverySummary {
  id: string;
  endpoint_id: string;
  event_id: string;
  event_type: string;
  status: string;
  attempts: number;
  response_status?: number | null;
  last_error?: string | null;
  next_attempt_at?: string | null;
  created_at: string;
  delivered_at?: string | null;
}

export class KurooApiError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(code: string, message: string, requestId?: string, details?: unknown) {
    super(message);
    this.name = "KurooApiError";
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export class KurooClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly credentials: RequestCredentials;

  constructor(options: KurooClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    // Browser implementations of `fetch` require the global object as their
    // receiver. Keep injected test/client implementations untouched, but bind
    // the native function so a client method call cannot fail with
    // "Illegal invocation" after it is stored on the instance.
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.credentials = options.credentials ?? "include";
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.baseUrl}${path}`, {
        ...init,
        credentials: this.credentials,
        headers: { "content-type": "application/json", ...(init.headers ?? {}) }
      });
    } catch (reason) {
      // Browser fetch errors are intentionally vague (usually just
      // "Failed to fetch").  Keep the public SDK contract typed so every
      // application can render an actionable message and preserve the
      // original abort semantics for callers that cancel stale requests.
      if (reason instanceof Error && reason.name === "AbortError") {
        throw new KurooApiError("REQUEST_ABORTED", "La solicitud fue cancelada.");
      }
      throw new KurooApiError("NETWORK_ERROR", "No se pudo conectar con la API de Kuroo. Verificá la URL del servicio y tu conexión.");
    }
    let payload: ApiResponse<T>;
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch {
      throw new KurooApiError("INVALID_API_RESPONSE", "La API devolvió una respuesta no válida. Revisá que el entorno esté configurado correctamente.", response.headers.get("x-request-id") ?? undefined);
    }
    if (!response.ok || "error" in payload) {
      const error = "error" in payload ? payload.error : { code: "HTTP_ERROR", message: response.statusText };
      throw new KurooApiError(error.code, error.message, error.requestId, error.details);
    }
    return payload.data;
  }

  /**
   * SCIM is deliberately kept separate from the Kuroo REST envelope: RFC
   * 7644 requires a bare resource/ListResponse and `application/scim+json`.
   * This helper still maps failures to the SDK's typed error so callers do not
   * need a second error-handling model.
   */
  private async requestScim<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers ?? {});
    headers.set("authorization", `Bearer ${token}`);
    if (init.body !== undefined && !headers.has("content-type")) headers.set("content-type", "application/scim+json");
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "omit",
      headers
    });
    const requestId = response.headers.get("x-request-id") ?? undefined;
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    let payload: unknown;
    try { payload = text ? JSON.parse(text) : undefined; } catch { payload = undefined; }
    if (!response.ok) {
      const record = payload && typeof payload === "object" && !Array.isArray(payload) ? payload as Record<string, unknown> : {};
      const scimType = typeof record.scimType === "string" ? record.scimType : "error";
      const detail = typeof record.detail === "string" ? record.detail : response.statusText || "SCIM request failed";
      throw new KurooApiError(`SCIM_${scimType.toUpperCase()}`, detail, requestId);
    }
    return payload as T;
  }

  health(): Promise<HealthResponse> {
    return this.request<HealthResponse>("/api/v1/health");
  }

  currentUser(): Promise<AuthUser> {
    return this.request<AuthUser>("/api/v1/auth/me");
  }

  login(email: string, password: string): Promise<LoginResult> {
    return this.request<LoginResult>("/api/v1/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  }

  changePassword(input: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<PasswordChangeResult> {
    return this.request<PasswordChangeResult>("/api/v1/auth/password/change", { method: "POST", body: JSON.stringify(input) });
  }

  recoverAdminPassword(input: { email: string; newPassword: string; confirmPassword: string; newEmail?: string }, recoveryToken: string): Promise<AdminPasswordRecoveryResult> {
    return this.request<AdminPasswordRecoveryResult>("/api/v1/auth/password/recover-admin", {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "X-Admin-Recovery-Token": recoveryToken }
    });
  }

  mfaCredentials(): Promise<MfaCredential[]> {
    return this.request<MfaCredential[]>("/api/v1/auth/mfa");
  }

  enrollMfa(input: { method: MfaMethod; label?: string }): Promise<MfaEnrollment & { challengeId: string; expiresAt: string; recoveryCodes?: string[] }> {
    return this.request<MfaEnrollment & { challengeId: string; expiresAt: string; recoveryCodes?: string[] }>("/api/v1/auth/mfa/enroll", { method: "POST", body: JSON.stringify(input) });
  }

  verifyMfa(input: { challengeId: string; challenge: string; code?: string; assertion?: Record<string, unknown> }): Promise<AuthUser> {
    return this.request<AuthUser>("/api/v1/auth/mfa/verify", { method: "POST", body: JSON.stringify(input) });
  }

  recoverMfa(input: { challengeId: string; code: string }): Promise<AuthUser> {
    return this.request<AuthUser>("/api/v1/auth/mfa/recovery", { method: "POST", body: JSON.stringify(input) });
  }

  disableMfa(password: string): Promise<{ disabled: boolean }> {
    return this.request<{ disabled: boolean }>("/api/v1/auth/mfa/disable", { method: "POST", body: JSON.stringify({ password }) });
  }

  sessions(): Promise<SessionSummary[]> {
    return this.request<SessionSummary[]>("/api/v1/auth/sessions");
  }

  revokeSession(sessionId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
    return this.request<{ id: string; revoked: boolean; current: boolean }>(`/api/v1/auth/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" });
  }

  revokeOtherSessions(): Promise<{ revoked: number; current: boolean }> {
    return this.request<{ revoked: number; current: boolean }>("/api/v1/auth/sessions", { method: "DELETE", body: "{}" });
  }

  devices(): Promise<DeviceSummary[]> {
    return this.request<DeviceSummary[]>("/api/v1/auth/devices");
  }

  revokeDevice(deviceId: string): Promise<{ id: string; revoked: boolean; current: boolean }> {
    return this.request<{ id: string; revoked: boolean; current: boolean }>(`/api/v1/auth/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE" });
  }

  logout(): Promise<{ loggedOut: boolean }> {
    return this.request<{ loggedOut: boolean }>("/api/v1/auth/logout", { method: "POST", body: "{}" });
  }

  users(): Promise<TeamUser[]> {
    return this.request<TeamUser[]>("/api/v1/admin/users");
  }

  createUser(input: { email: string; firstName: string; lastName?: string; role?: AuthUser["role"]; password?: string }): Promise<TeamUser> {
    return this.request<TeamUser>("/api/v1/admin/users", { method: "POST", body: JSON.stringify(input) });
  }

  providersAdmin(filters: { search?: string; status?: "active" | "inactive" } = {}): Promise<ProviderSummary[]> {
    const query = new URLSearchParams();
    if (filters.search) query.set("search", filters.search);
    if (filters.status) query.set("status", filters.status);
    return this.request<ProviderSummary[]>(`/api/v1/admin/providers${query.size ? `?${query}` : ""}`);
  }

  locationsAdmin(filters: { status?: CatalogLocation["status"] } = {}): Promise<CatalogLocation[]> {
    const query = new URLSearchParams();
    if (filters.status) query.set("status", filters.status);
    return this.request<CatalogLocation[]>(`/api/v1/admin/locations${query.size ? `?${query}` : ""}`);
  }

  createProvider(input: ProviderInput): Promise<ProviderDetail> {
    return this.request<ProviderDetail>("/api/v1/admin/providers", {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Idempotency-Key": `provider-create-${crypto.randomUUID()}` }
    });
  }

  provider(providerId: string): Promise<ProviderDetail> {
    return this.request<ProviderDetail>(`/api/v1/admin/providers/${encodeURIComponent(providerId)}`);
  }

  updateProvider(providerId: string, input: Partial<ProviderInput>): Promise<ProviderDetail> {
    return this.request<ProviderDetail>(`/api/v1/admin/providers/${encodeURIComponent(providerId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
      headers: { "Idempotency-Key": `provider-update-${providerId}-${crypto.randomUUID()}` }
    });
  }

  async uploadProviderPhoto(providerId: string, file: File): Promise<ProviderDetail> {
    const form = new FormData();
    form.set("file", file);
    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/admin/providers/${encodeURIComponent(providerId)}/photo`, {
      method: "POST",
      credentials: this.credentials,
      body: form
    });
    let payload: ApiResponse<ProviderDetail>;
    try {
      payload = (await response.json()) as ApiResponse<ProviderDetail>;
    } catch {
      throw new KurooApiError("INVALID_API_RESPONSE", "La API devolvió una respuesta no válida.", response.headers.get("x-request-id") ?? undefined);
    }
    if (!response.ok || "error" in payload) {
      const error = "error" in payload ? payload.error : { code: "UPLOAD_FAILED", message: "No se pudo guardar la foto del proveedor." };
      throw new KurooApiError(error.code, error.message, error.requestId, error.details);
    }
    return payload.data;
  }

  updateUser(userId: string, input: { firstName?: string; lastName?: string; role?: AuthUser["role"]; status?: AuthUser["status"] }): Promise<Partial<TeamUser> & { id: string }> {
    return this.request<Partial<TeamUser> & { id: string }>(`/api/v1/admin/users/${encodeURIComponent(userId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  services(options: { signal?: AbortSignal } = {}): Promise<ServiceSummary[]> {
    return this.request<ServiceSummary[]>("/api/v1/catalog/services", { signal: options.signal });
  }

  createService(input: { name: string; durationMinutes: number; priceCents: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean }): Promise<ServiceSummary> {
    return this.request<ServiceSummary>("/api/v1/catalog/services", { method: "POST", body: JSON.stringify(input) });
  }

  service(serviceId: string): Promise<ServiceSummary> {
    return this.request<ServiceSummary>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`);
  }

  updateService(serviceId: string, input: { name?: string; slug?: string; description?: string | null; durationMinutes?: number; priceCents?: number; currency?: string; capacity?: number; priceUnit?: ServiceSummary["priceUnit"]; status?: ServiceSummary["status"]; categoryId?: string | null; paymentMode?: ServicePaymentMode; googleMeetEnabled?: boolean; recurrence?: Partial<ServiceRecurrenceSettings> }): Promise<ServiceSummary> {
    return this.request<ServiceSummary>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteService(serviceId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return this.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/services/${encodeURIComponent(serviceId)}`, { method: "DELETE" });
  }

  pricingRules(filters: { targetKind?: PricingRule["targetKind"]; targetId?: string; enabled?: boolean } = {}): Promise<PricingRule[]> {
    const query = new URLSearchParams();
    if (filters.targetKind) query.set("targetKind", filters.targetKind);
    if (filters.targetId) query.set("targetId", filters.targetId);
    if (filters.enabled !== undefined) query.set("enabled", String(filters.enabled));
    return this.request<PricingRule[]>(`/api/v1/pricing/rules${query.size ? `?${query}` : ""}`);
  }

  createPricingRule(input: PricingRuleInput): Promise<PricingRule> {
    return this.request<PricingRule>("/api/v1/pricing/rules", { method: "POST", body: JSON.stringify(input) });
  }

  updatePricingRule(ruleId: string, input: PricingRulePatch): Promise<PricingRule> {
    return this.request<PricingRule>(`/api/v1/pricing/rules/${encodeURIComponent(ruleId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deletePricingRule(ruleId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/pricing/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
  }

  quoteService(input: ServiceQuoteRequest, options: { signal?: AbortSignal } = {}): Promise<PriceQuote> {
    return this.request<PriceQuote>("/api/v1/pricing/quote", { method: "POST", body: JSON.stringify(input), signal: options.signal, headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
  }

  /** Generic quote facade retained for integrations that pass a forward-compatible payload. */
  quote(input: Record<string, unknown> & { idempotencyKey?: string }): Promise<PriceQuote> {
    return this.request<PriceQuote>("/api/v1/pricing/quote", { method: "POST", body: JSON.stringify(input), headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
  }

  categories(options: { includeArchived?: boolean; status?: CatalogCategory["status"] } = {}): Promise<CatalogCategory[]> {
    const query = new URLSearchParams();
    if (options.includeArchived) query.set("includeArchived", "true");
    if (options.status) query.set("status", options.status);
    return this.request<CatalogCategory[]>(`/api/v1/catalog/categories${query.size ? `?${query}` : ""}`);
  }

  createCategory(input: { name: string; slug?: string; description?: string; sortOrder?: number; status?: CatalogCategory["status"] }): Promise<Pick<CatalogCategory, "id" | "name" | "slug">> {
    return this.request<Pick<CatalogCategory, "id" | "name" | "slug">>("/api/v1/catalog/categories", { method: "POST", body: JSON.stringify(input) });
  }

  category(categoryId: string): Promise<CatalogCategory> {
    return this.request<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`);
  }

  updateCategory(categoryId: string, input: { name?: string; slug?: string; description?: string | null; status?: CatalogCategory["status"]; sortOrder?: number }): Promise<CatalogCategory> {
    return this.request<CatalogCategory>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteCategory(categoryId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return this.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/categories/${encodeURIComponent(categoryId)}`, { method: "DELETE" });
  }

  taxes(options: { status?: TaxDefinition["status"]; signal?: AbortSignal } = {}): Promise<TaxDefinition[]> {
    const query = new URLSearchParams();
    if (options.status) query.set("status", options.status);
    return this.request<TaxDefinition[]>(`/api/v1/taxes${query.size ? `?${query}` : ""}`, { signal: options.signal });
  }

  createTax(input: TaxInput): Promise<TaxDefinition> {
    return this.request<TaxDefinition>("/api/v1/taxes", { method: "POST", body: JSON.stringify(input) });
  }

  tax(taxId: string): Promise<TaxDefinition> {
    return this.request<TaxDefinition>(`/api/v1/taxes/${encodeURIComponent(taxId)}`);
  }

  updateTax(taxId: string, input: TaxPatch): Promise<TaxDefinition> {
    return this.request<TaxDefinition>(`/api/v1/taxes/${encodeURIComponent(taxId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteTax(taxId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/taxes/${encodeURIComponent(taxId)}`, { method: "DELETE" });
  }

  updateTaxStatus(taxId: string, status: TaxDefinition["status"]): Promise<{ id: string; status: TaxDefinition["status"] }> {
    return this.request<{ id: string; status: TaxDefinition["status"] }>(`/api/v1/taxes/${encodeURIComponent(taxId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
  }

  customFields(options: { entityType?: CustomFieldDefinition["entityType"]; status?: CustomFieldDefinition["status"]; includeInactive?: boolean; signal?: AbortSignal } = {}): Promise<CustomFieldDefinition[]> {
    const query = new URLSearchParams();
    if (options.entityType) query.set("entityType", options.entityType);
    if (options.status) query.set("status", options.status);
    if (options.includeInactive) query.set("includeInactive", "true");
    return this.request<CustomFieldDefinition[]>(`/api/v1/custom-fields${query.size ? `?${query}` : ""}`, { signal: options.signal });
  }

  createCustomField(input: CustomFieldInput): Promise<CustomFieldDefinition> {
    return this.request<CustomFieldDefinition>("/api/v1/custom-fields", { method: "POST", body: JSON.stringify(input) });
  }

  customField(fieldId: string): Promise<CustomFieldDefinition> {
    return this.request<CustomFieldDefinition>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`);
  }

  updateCustomField(fieldId: string, input: CustomFieldPatch): Promise<CustomFieldDefinition> {
    return this.request<CustomFieldDefinition>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteCustomField(fieldId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}`, { method: "DELETE" });
  }

  customFieldValue(fieldId: string, entityId: string): Promise<CustomFieldValue> {
    return this.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`);
  }

  setCustomFieldValue(fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
    return this.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "PUT", body: JSON.stringify(input) });
  }

  updateCustomFieldValue(fieldId: string, entityId: string, input: CustomFieldValueInput): Promise<CustomFieldValue> {
    return this.request<CustomFieldValue>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteCustomFieldValue(fieldId: string, entityId: string): Promise<{ fieldId: string; entityId: string; deleted: boolean }> {
    return this.request<{ fieldId: string; entityId: string; deleted: boolean }>(`/api/v1/custom-fields/${encodeURIComponent(fieldId)}/values/${encodeURIComponent(entityId)}`, { method: "DELETE" });
  }

  settings(): Promise<Array<{ key: string; value: unknown; encrypted: boolean; updatedAt: string }>> {
    return this.request<Array<{ key: string; value: unknown; encrypted: boolean; updatedAt: string }>>("/api/v1/settings");
  }

  updateSettings(settings: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/api/v1/settings", { method: "PATCH", body: JSON.stringify({ settings }) });
  }

  conciergeConfig(): Promise<ConciergeConfigResponse> {
    return this.request<ConciergeConfigResponse>("/api/v1/concierge/config");
  }

  updateConciergeConfig(config: ConciergeConfigPatch): Promise<ConciergeConfigResponse> {
    return this.request<ConciergeConfigResponse>("/api/v1/concierge/config", { method: "PATCH", body: JSON.stringify(config) });
  }

  slots(serviceId: string, date: string, options: { providerId?: string; locationId?: string; extras?: BookingExtraSelection[]; persons?: number; includePricing?: boolean; signal?: AbortSignal } = {}): Promise<AvailableSlot[]> {
    const query = new URLSearchParams({ serviceId, date });
    if (options.providerId) query.set("providerId", options.providerId);
    if (options.locationId) query.set("locationId", options.locationId);
    if (options.extras?.length) query.set("extras", JSON.stringify(options.extras));
    if (options.persons) query.set("persons", String(options.persons));
    if (options.includePricing) query.set("includePricing", "true");
    return this.request<AvailableSlot[]>(`/api/v1/availability/slots?${query.toString()}`, { signal: options.signal });
  }

  nextAvailability(serviceId: string, options: { fromDate?: string; days?: number; providerId?: string; locationId?: string; signal?: AbortSignal } = {}): Promise<AvailabilityNextResponse> {
    const query = new URLSearchParams({ serviceId });
    if (options.fromDate) query.set("fromDate", options.fromDate);
    if (options.days) query.set("days", String(options.days));
    if (options.providerId) query.set("providerId", options.providerId);
    if (options.locationId) query.set("locationId", options.locationId);
    return this.request<AvailabilityNextResponse>(`/api/v1/availability/next?${query.toString()}`, { signal: options.signal });
  }

  holdSlot(input: SlotHoldRequest): Promise<SlotHoldResult> {
    return this.request<SlotHoldResult>("/api/v1/availability/hold", {
      method: "POST",
      body: JSON.stringify(input)
    });
  }

  releaseHold(holdId: string, slotKey: string): Promise<{ released: boolean }> {
    return this.request<{ released: boolean }>("/api/v1/availability/release", {
      method: "POST",
      body: JSON.stringify({ holdId, slotKey })
    });
  }

  createBooking(input: BookingRequest): Promise<Booking | BookingCheckout> {
    return this.request<Booking | BookingCheckout>("/api/v1/bookings", {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Idempotency-Key": input.idempotencyKey }
    });
  }

  createAdminBooking(input: StaffBookingRequest): Promise<Booking | BookingCheckout> {
    return this.request<Booking | BookingCheckout>("/api/v1/admin/bookings", {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Idempotency-Key": input.idempotencyKey }
    });
  }

  recurringSeries(): Promise<RecurringSeries[]> {
    return this.request<RecurringSeries[]>("/api/v1/recurring-series");
  }

  recurringSeriesDetail(seriesId: string): Promise<RecurringSeries> {
    return this.request<RecurringSeries>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}`);
  }

  previewRecurringSeries(input: RecurringSeriesPreviewInput, options: { signal?: AbortSignal } = {}): Promise<RecurringSeriesPreview> {
    return this.request<RecurringSeriesPreview>("/api/v1/recurring-series/preview", { method: "POST", body: JSON.stringify(input), signal: options.signal });
  }

  checkoutRecurringSeries(input: RecurringSeriesCheckoutInput): Promise<RecurringSeriesCheckout> {
    return this.request<RecurringSeriesCheckout>("/api/v1/recurring-series/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  createRecurringSeries(input: RecurringSeriesInput): Promise<RecurringSeries> {
    return this.request<RecurringSeries>("/api/v1/recurring-series", { method: "POST", body: JSON.stringify(input) });
  }

  cancelRecurringSeries(seriesId: string, input: { scope?: "occurrence" | "future" | "all"; occurrenceIds?: string[]; reason?: string } = {}): Promise<{ id: string; status: string; refundId?: string | null; refundPending?: boolean }> {
    return this.request<{ id: string; status: string; refundId?: string | null; refundPending?: boolean }>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}/cancel`, { method: "POST", body: JSON.stringify(input) });
  }

  rescheduleRecurringOccurrence(seriesId: string, occurrenceId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string | null; locationId?: string | null; idempotencyKey: string }): Promise<RecurringSeries> {
    return this.request<RecurringSeries>(`/api/v1/recurring-series/${encodeURIComponent(seriesId)}/occurrences/${encodeURIComponent(occurrenceId)}/reschedule`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  createRecurringBookings(seriesId: string, occurrences: RecurringBookingOccurrence[], options: { persons?: number; notes?: string; idempotencyKey?: string } = {}): Promise<{ seriesId: string; bookings: Booking[] }> {
    const idempotencyKey = options.idempotencyKey ?? `recurring-${seriesId}-${Date.now()}`;
    return this.request<{ seriesId: string; bookings: Booking[] }>("/api/v1/bookings/recurring", {
      method: "POST",
      body: JSON.stringify({ seriesId, occurrences, persons: options.persons, notes: options.notes, idempotencyKey }),
      headers: { "Idempotency-Key": idempotencyKey }
    });
  }

  bookingParticipants(bookingId: string): Promise<BookingParticipant[]> {
    return this.request<BookingParticipant[]>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants`);
  }

  addBookingParticipant(bookingId: string, input: BookingParticipantInput): Promise<BookingParticipant> {
    return this.request<BookingParticipant>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants`, { method: "POST", body: JSON.stringify(input) });
  }

  removeBookingParticipant(bookingId: string, participantId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/participants/${encodeURIComponent(participantId)}`, { method: "DELETE" });
  }

  reassignBooking(bookingId: string, input: { slotKey: string; startAt: string; endAt: string; providerId?: string; locationId?: string; idempotencyKey?: string }): Promise<Booking> {
    const idempotencyKey = input.idempotencyKey ?? `reassign-${bookingId}-${Date.now()}`;
    return this.request<Booking>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/reassign`, { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
  }

  createBookingAction(bookingId: string, action: "cancel" = "cancel", ttlSeconds?: number): Promise<SignedAction> {
    return this.request<SignedAction>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/signed-action`, { method: "POST", body: JSON.stringify({ action, ttlSeconds }) });
  }

  executeSignedAction(token: string): Promise<{ action: string; id: string; status: string }> {
    return this.request<{ action: string; id: string; status: string }>(`/api/v1/signed-actions/${encodeURIComponent(token)}`, { method: "POST", body: "{}" });
  }

  bookings(): Promise<Booking[]> {
    return this.request<Booking[]>("/api/v1/bookings");
  }

  booking(bookingId: string): Promise<Booking> {
    return this.request<Booking>(`/api/v1/bookings/${encodeURIComponent(bookingId)}`);
  }

  updateBooking(bookingId: string, input: BookingUpdateInput): Promise<BookingUpdateResult> {
    return this.request<BookingUpdateResult>(`/api/v1/bookings/${encodeURIComponent(bookingId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
      headers: { "Idempotency-Key": input.idempotencyKey }
    });
  }

  dashboardSummary(range: { from?: string; to?: string } = {}): Promise<DashboardSummary> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<DashboardSummary>(`/api/v1/dashboard/summary${query.size ? `?${query}` : ""}`);
  }

  dashboardOverview(filters: DashboardOverviewQuery = {}): Promise<DashboardOverview> {
    const query = new URLSearchParams();
    if (filters.view) query.set("view", filters.view);
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    if (filters.month) query.set("month", filters.month);
    if (filters.serviceId) query.set("serviceId", filters.serviceId);
    if (filters.providerId) query.set("providerId", filters.providerId);
    if (filters.locationId) query.set("locationId", filters.locationId);
    if (filters.upcomingStatus) query.set("upcomingStatus", filters.upcomingStatus);
    return this.request<DashboardOverview>(`/api/v1/dashboard/overview${query.size ? `?${query}` : ""}`);
  }

  reportOverview(range: { from?: string; to?: string } = {}): Promise<ReportOverview> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<ReportOverview>(`/api/v1/reports/overview${query.size ? `?${query}` : ""}`);
  }

  reportRevenue(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportRevenuePoint[] }> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<{ range: { from: string; to: string }; points: ReportRevenuePoint[] }>(`/api/v1/reports/revenue${query.size ? `?${query}` : ""}`);
  }

  reportBookings(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; points: ReportBookingPoint[] }> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<{ range: { from: string; to: string }; points: ReportBookingPoint[] }>(`/api/v1/reports/bookings${query.size ? `?${query}` : ""}`);
  }

  mobileBootstrap(): Promise<MobileBootstrap> {
    return this.request<MobileBootstrap>("/api/v1/mobile/bootstrap");
  }

  mobileAppointments(range: { from?: string; to?: string } = {}): Promise<{ range: { from: string; to: string }; appointments: Booking[] }> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<{ range: { from: string; to: string }; appointments: Booking[] }>(`/api/v1/mobile/appointments${query.size ? `?${query}` : ""}`);
  }

  mobileEvents(range: { from?: string; to?: string } = {}): Promise<EventSummary[]> {
    const query = new URLSearchParams();
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return this.request<EventSummary[]>(`/api/v1/mobile/events${query.size ? `?${query}` : ""}`);
  }

  calendar(filters: { from?: string; to?: string; providerId?: string; locationId?: string; serviceId?: string; includeEvents?: boolean } = {}): Promise<CalendarResponse> {
    const query = new URLSearchParams();
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    if (filters.providerId) query.set("providerId", filters.providerId);
    if (filters.locationId) query.set("locationId", filters.locationId);
    if (filters.serviceId) query.set("serviceId", filters.serviceId);
    if (filters.includeEvents) query.set("includeEvents", "true");
    return this.request<CalendarResponse>(`/api/v1/calendar${query.size ? `?${query}` : ""}`);
  }

  customers(search?: string): Promise<CustomerSummary[]> {
    const query = search ? `?q=${encodeURIComponent(search)}` : "";
    return this.request<CustomerSummary[]>(`/api/v1/customers${query}`);
  }

  customer(customerId: string): Promise<CustomerSummary & { bookings: Booking[] }> {
    return this.request<CustomerSummary & { bookings: Booking[] }>(`/api/v1/customers/${encodeURIComponent(customerId)}`);
  }

  updateCustomer(customerId: string, input: { firstName?: string; lastName?: string; phone?: string; timezone?: string; locale?: string }): Promise<CustomerSummary & { bookings: Booking[] }> {
    return this.request<CustomerSummary & { bookings: Booking[] }>(`/api/v1/customers/${encodeURIComponent(customerId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  customerNotificationPreferences(customerId: string): Promise<NotificationPreferences> {
    return this.request<NotificationPreferences>(`/api/v1/customers/${encodeURIComponent(customerId)}/notification-preferences`);
  }

  updateCustomerNotificationPreferences(customerId: string, input: Partial<Omit<NotificationPreferences, "userId" | "updatedAt">>): Promise<NotificationPreferences> {
    return this.request<NotificationPreferences>(`/api/v1/customers/${encodeURIComponent(customerId)}/notification-preferences`, { method: "PATCH", body: JSON.stringify(input) });
  }

  payments(filters: { from?: string; to?: string; status?: string } = {}): Promise<PaymentSummary[]> {
    const query = new URLSearchParams();
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    if (filters.status) query.set("status", filters.status);
    return this.request<PaymentSummary[]>(`/api/v1/payments${query.size ? `?${query}` : ""}`);
  }

  createPaymentCheckout(input: { provider?: PaymentProviderName; integrationAccountId?: string; sourceId?: string; bookingId?: string; invoiceId?: string; recurringSeriesId?: string; paymentLinkToken?: string; environment?: PaymentProviderEnvironment; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey: string }): Promise<PaymentCheckout> {
    return this.request<PaymentCheckout>("/api/v1/payments/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  payment(paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
    return this.request<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`);
  }

  updatePayment(paymentId: string, input: { status?: "pending" | "authorized" | "paid" | "partially_refunded" | "refunded" | "failed" | "cancelled"; statusDetail?: string | null }): Promise<{ id: string; status: string; statusDetail: string | null }> {
    return this.request<{ id: string; status: string; statusDetail: string | null }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deletePayment(paymentId: string): Promise<{ id: string; status: "cancelled"; deleted: boolean }> {
    return this.request<{ id: string; status: "cancelled"; deleted: boolean }>(`/api/v1/payments/${encodeURIComponent(paymentId)}`, { method: "DELETE" });
  }

  calculatePaymentAmount(input: { subtotalCents?: number; amountCents?: number; currency?: string; couponCode?: string }): Promise<{ subtotalCents: number; discountCents: number; totalCents: number; currency: string }> {
    return this.request<{ subtotalCents: number; discountCents: number; totalCents: number; currency: string }>("/api/v1/payments/amount", { method: "POST", body: JSON.stringify(input) });
  }

  paymentTransaction(transactionId: string): Promise<{ id: string; paymentId: string; kind: string; amountCents: number; providerTransactionId?: string | null; status: string; currency: string; createdAt: string }> {
    return this.request<{ id: string; paymentId: string; kind: string; amountCents: number; providerTransactionId?: string | null; status: string; currency: string; createdAt: string }>(`/api/v1/payments/transactions/${encodeURIComponent(transactionId)}`);
  }

  refundPaymentById(paymentId: string, input: { amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
    return this.request<RefundSummary>(`/api/v1/payments/${encodeURIComponent(paymentId)}/refund`, { method: "POST", body: JSON.stringify({ ...input, paymentId }), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  approveRefund(refundId: string, input: { method: "mercadopago" | "cash"; amountCents?: number; overrideReason?: string; idempotencyKey: string }): Promise<RefundSummary> {
    return this.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/approve`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  rejectRefund(refundId: string, reason: string): Promise<RefundSummary> {
    return this.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/reject`, { method: "POST", body: JSON.stringify({ reason }) });
  }

  completeCashRefund(refundId: string, cashReceiptReference: string, idempotencyKey = `cash-delivery:${refundId}:${cashReceiptReference}`): Promise<RefundSummary> {
    return this.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/cash-delivered`, { method: "POST", body: JSON.stringify({ cashReceiptReference, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
  }

  retryRefund(refundId: string, idempotencyKey: string): Promise<RefundSummary> {
    return this.request<RefundSummary>(`/api/v1/payments/refunds/${encodeURIComponent(refundId)}/retry`, { method: "POST", body: JSON.stringify({ idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
  }

  refunds(filters: { status?: string } = {}): Promise<RefundSummary[]> {
    const query = new URLSearchParams();
    if (filters.status) query.set("status", filters.status);
    return this.request<RefundSummary[]>(`/api/v1/payments/refunds${query.size ? `?${query}` : ""}`);
  }

  capturePayment(paymentId: string): Promise<{ paymentId: string; provider: string; providerPaymentId: string; status: string; statusDetail?: string | null; amountCents: number; currency: string }> {
    return this.request<{ paymentId: string; provider: string; providerPaymentId: string; status: string; statusDetail?: string | null; amountCents: number; currency: string }>(`/api/v1/payments/${encodeURIComponent(paymentId)}/capture`, { method: "POST", body: "{}" });
  }

  paymentTransactions(paymentId: string): Promise<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }> {
    return this.request<{ payment: PaymentSummary; transactions: PaymentTransactionSummary[]; refunds: RefundSummary[] }>(`/api/v1/payments/${encodeURIComponent(paymentId)}/transactions`);
  }

  invoices(): Promise<InvoiceSummary[]> {
    return this.request<InvoiceSummary[]>("/api/v1/invoices");
  }

  invoice(invoiceId: string): Promise<InvoiceSummary> {
    return this.request<InvoiceSummary>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}`);
  }

  createInvoice(input: { bookingId: string; taxRateBps?: number; number?: string; dueAt?: string }): Promise<InvoiceSummary> {
    return this.request<InvoiceSummary>("/api/v1/invoices", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": `invoice-${input.bookingId}` } });
  }

  voidInvoice(invoiceId: string): Promise<{ id: string; status: "void" }> {
    return this.request<{ id: string; status: "void" }>(`/api/v1/invoices/${encodeURIComponent(invoiceId)}/void`, { method: "POST", body: "{}" });
  }

  bookingDeposit(bookingId: string): Promise<BookingDeposit | null> {
    return this.request<BookingDeposit | null>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/deposit`);
  }

  createBookingDeposit(input: { bookingId: string; amountCents: number; dueAt?: string }): Promise<BookingDeposit> {
    return this.request<BookingDeposit>(`/api/v1/bookings/${encodeURIComponent(input.bookingId)}/deposit`, { method: "POST", body: JSON.stringify(input) });
  }

  createPaymentLink(input: { bookingId?: string; invoiceId?: string; amountCents?: number; ttlSeconds?: number }): Promise<PaymentLink> {
    return this.request<PaymentLink>("/api/v1/payments/links", { method: "POST", body: JSON.stringify(input) });
  }

  paymentLink(token: string): Promise<PaymentLink> {
    return this.request<PaymentLink>(`/api/v1/payments/links/${encodeURIComponent(token)}`);
  }

  paymentLinkStatus(token: string): Promise<PaymentLinkStatusResponse> {
    return this.request<PaymentLinkStatusResponse>(`/api/v1/payments/links/${encodeURIComponent(token)}/status`);
  }

  cancelPaymentLink(linkId: string): Promise<{ id: string; status: "cancelled" }> {
    return this.request<{ id: string; status: "cancelled" }>(`/api/v1/payments/links/${encodeURIComponent(linkId)}/cancel`, { method: "POST", body: "{}" });
  }

  notifications(options: { status?: string; channel?: NotificationChannel; definitionId?: string; recipient?: string } = {}): Promise<NotificationSummary[]> {
    const query = new URLSearchParams();
    if (options.status) query.set("status", options.status);
    if (options.channel) query.set("channel", options.channel);
    if (options.definitionId) query.set("definitionId", options.definitionId);
    if (options.recipient) query.set("recipient", options.recipient);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request<NotificationSummary[]>(`/api/v1/notifications${suffix}`);
  }

  notificationCatalog(): Promise<NotificationCatalog> {
    return this.request<NotificationCatalog>("/api/v1/notification-definitions");
  }

  createNotificationDefinition(input: NotificationDefinitionInput): Promise<NotificationDefinition> {
    return this.request<NotificationDefinition>("/api/v1/notification-definitions", { method: "POST", body: JSON.stringify(input) });
  }

  updateNotificationDefinition(definitionId: string, input: NotificationDefinitionUpdate): Promise<NotificationDefinition> {
    return this.request<NotificationDefinition>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteNotificationDefinition(definitionId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}`, { method: "DELETE", body: "{}" });
  }

  updateNotificationDefinitionTemplate(definitionId: string, channel: NotificationChannel, input: NotificationTemplateUpdate): Promise<NotificationTemplate> {
    return this.request<NotificationTemplate>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}/templates/${encodeURIComponent(channel)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  sendNotificationTest(definitionId: string, input: { channel: Exclude<NotificationChannel, "push">; recipient: string; variables?: Record<string, unknown> }): Promise<NotificationSummary> {
    return this.request<NotificationSummary>(`/api/v1/notification-definitions/${encodeURIComponent(definitionId)}/test`, { method: "POST", body: JSON.stringify(input) });
  }

  notificationTemplates(): Promise<NotificationTemplate[]> {
    return this.request<NotificationTemplate[]>("/api/v1/notification-templates");
  }

  createNotificationTemplate(input: { channel: NotificationChannel; eventType: string; locale?: string; subject?: string; body: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<NotificationTemplate> {
    return this.request<NotificationTemplate>("/api/v1/notification-templates", { method: "POST", body: JSON.stringify(input) });
  }

  updateNotificationTemplate(templateId: string, input: { channel?: NotificationChannel; subject?: string | null; body?: string; enabled?: boolean; format?: "html" | "text"; whatsappTemplate?: Record<string, unknown> | null }): Promise<{ id: string; updated: boolean }> {
    return this.request<{ id: string; updated: boolean }>(`/api/v1/notification-templates/${encodeURIComponent(templateId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  sendNotification(input: { channel?: NotificationChannel; templateId?: string; bookingId?: string; recipient: string; scheduledAt?: string; variables?: Record<string, unknown>; idempotencyKey?: string }): Promise<{ id: string; channel: NotificationChannel; recipient: string; status: "queued"; scheduledAt: string }> {
    const idempotencyKey = input.idempotencyKey ?? `notification-${Date.now()}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`}`;
    return this.request<{ id: string; channel: NotificationChannel; recipient: string; status: "queued"; scheduledAt: string }>("/api/v1/notifications", { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
  }

  retryNotification(deliveryId: string): Promise<{ id: string; status: "queued" }> {
    return this.request<{ id: string; status: "queued" }>(`/api/v1/notifications/${encodeURIComponent(deliveryId)}/retry`, { method: "POST", body: "{}" });
  }

  integrations(): Promise<IntegrationSummary[]> {
    return this.request<IntegrationSummary[]>("/api/v1/integrations");
  }

  integrationCatalog(): Promise<IntegrationCatalogEntry[]> {
    return this.request<IntegrationCatalogEntry[]>("/api/v1/integrations/catalog");
  }

  createIntegration(input: { provider: string; accountLabel: string; credentials: Record<string, unknown>; environment?: PaymentProviderEnvironment; config?: { countryCode?: string; currency?: string } }): Promise<IntegrationSummary> {
    return this.request<IntegrationSummary>("/api/v1/integrations", { method: "POST", body: JSON.stringify(input) });
  }

  updateIntegration(integrationId: string, input: { accountLabel?: string; credentials?: Record<string, unknown>; environment?: PaymentProviderEnvironment; config?: { countryCode?: string; currency?: string } }): Promise<IntegrationSummary> {
    return this.request<IntegrationSummary>(`/api/v1/integrations/${encodeURIComponent(integrationId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteIntegration(integrationId: string): Promise<{ id: string; deleted: true }> {
    return this.request<{ id: string; deleted: true }>(`/api/v1/integrations/${encodeURIComponent(integrationId)}`, { method: "DELETE" });
  }

  googleCalendarAuthorizationUrl(integrationId: string): Promise<{ provider: "google-calendar"; integrationId: string; authUrl: string; authorizationUrl: string; stateExpiresAt: string }> {
    return this.request<{ provider: "google-calendar"; integrationId: string; authUrl: string; authorizationUrl: string; stateExpiresAt: string }>(`/api/v1/google-calendar/authorization/url/${encodeURIComponent(integrationId)}`);
  }

  googleCalendarList(integrationId: string): Promise<{ provider: "google-calendar"; integrationId: string; calendars: Array<{ id: string; url: string; name?: string; description?: string }> }> {
    return this.request<{ provider: "google-calendar"; integrationId: string; calendars: Array<{ id: string; url: string; name?: string; description?: string }> }>(`/api/v1/google-calendar/calendar-list/${encodeURIComponent(integrationId)}`);
  }

  integrationCalendarBindings(integrationId: string): Promise<IntegrationAccountBinding[]> {
    return this.request<IntegrationAccountBinding[]>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/providers`);
  }

  updateIntegrationCalendarBindings(integrationId: string, providerIds: string[]): Promise<IntegrationAccountBinding[]> {
    return this.request<IntegrationAccountBinding[]>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/providers`, { method: "PUT", body: JSON.stringify({ providerIds }) });
  }

  disableIntegration(integrationId: string): Promise<{ id: string; status: "disabled" }> {
    return this.request<{ id: string; status: "disabled" }>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/disable`, { method: "POST", body: "{}" });
  }

  enableIntegration(integrationId: string): Promise<{ id: string; status: "active"; lastTestStatus?: string }> {
    return this.request<{ id: string; status: "active"; lastTestStatus?: string }>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/enable`, { method: "POST", body: "{}" });
  }

  credentialVersions(integrationId: string): Promise<CredentialVersion[]> {
    return this.request<CredentialVersion[]>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/credentials/versions`);
  }

  revokeCredentialVersion(integrationId: string, version: number): Promise<{ id: string; integrationAccountId: string; version: number; status: "revoked" }> {
    return this.request<{ id: string; integrationAccountId: string; version: number; status: "revoked" }>(`/api/v1/integrations/${encodeURIComponent(integrationId)}/credentials/versions/${encodeURIComponent(version)}/revoke`, { method: "POST", body: "{}" });
  }

  testIntegration(provider: string, integrationId?: string, options?: { live?: boolean }): Promise<{ provider: string; configured: boolean; status: string; checkedAt: string; sandbox: boolean; connectionTest?: "live" | "configuration" | "unavailable"; adapterAvailable?: boolean; supportedOperations?: string[]; account?: unknown }> {
    return this.request<{ provider: string; configured: boolean; status: string; checkedAt: string; sandbox: boolean; connectionTest?: "live" | "configuration" | "unavailable"; adapterAvailable?: boolean; supportedOperations?: string[]; account?: unknown }>(`/api/v1/integrations/${encodeURIComponent(provider)}/test`, { method: "POST", body: JSON.stringify({ ...(integrationId ? { integrationId } : {}), ...(options?.live ? { live: true } : {}) }) });
  }

  ssoConnections(): Promise<SsoConnection[]> {
    return this.request<SsoConnection[]>("/api/v1/auth/sso/connections");
  }

  createSsoConnection(input: { name: string; protocol: "oidc" | "saml"; issuer?: string; clientId?: string; clientSecret?: string; metadataUrl?: string; authorizationUrl?: string; tokenUrl?: string; jwksUrl?: string; entityId?: string; certificatePem?: string; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
    return this.request<SsoConnection>("/api/v1/auth/sso/connections", { method: "POST", body: JSON.stringify(input) });
  }

  updateSsoConnection(connectionId: string, input: { name?: string; issuer?: string | null; clientId?: string | null; clientSecret?: string; metadataUrl?: string | null; authorizationUrl?: string | null; tokenUrl?: string | null; jwksUrl?: string | null; entityId?: string | null; certificatePem?: string | null; status?: "active" | "disabled"; claimMapping?: Record<string, string>; groupRoleMapping?: Partial<Record<"admin" | "manager" | "provider" | "customer", string[]>> }): Promise<SsoConnection> {
    return this.request<SsoConnection>(`/api/v1/auth/sso/connections/${encodeURIComponent(connectionId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  testSsoConnection(connectionId: string): Promise<{ id: string; protocol: "oidc" | "saml"; status: "ok" | "error"; checks: Record<string, string>; error: string | null }> {
    return this.request<{ id: string; protocol: "oidc" | "saml"; status: "ok" | "error"; checks: Record<string, string>; error: string | null }>(`/api/v1/auth/sso/connections/${encodeURIComponent(connectionId)}/test`, { method: "POST", body: "{}" });
  }

  ssoStartUrl(connectionId: string, redirectUri?: string): string {
    const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : "";
    return `${this.baseUrl}/api/v1/auth/sso/${encodeURIComponent(connectionId)}/start${query}`;
  }

  apiKeys(): Promise<ApiKeySummary[]> {
    return this.request<ApiKeySummary[]>("/api/v1/admin/api-keys");
  }

  createApiKey(input: { name: string; scopes?: ApiKeyScope[]; expiresAt?: string | null }): Promise<ApiKeyCreated> {
    return this.request<ApiKeyCreated>("/api/v1/admin/api-keys", { method: "POST", body: JSON.stringify(input) });
  }

  revokeApiKey(keyId: string): Promise<{ id: string; status: "revoked" }> {
    return this.request<{ id: string; status: "revoked" }>(`/api/v1/admin/api-keys/${encodeURIComponent(keyId)}/revoke`, { method: "POST", body: "{}" });
  }

  scimTokens(): Promise<ScimTokenSummary[]> {
    return this.request<ScimTokenSummary[]>("/api/v1/admin/scim-tokens");
  }

  createScimToken(input: { name: string; scopes?: string[]; expiresAt?: string | null }): Promise<ScimTokenCreated> {
    return this.request<ScimTokenCreated>("/api/v1/admin/scim-tokens", { method: "POST", body: JSON.stringify(input) });
  }

  revokeScimToken(tokenId: string): Promise<{ id: string; status: "revoked" }> {
    return this.request<{ id: string; status: "revoked" }>(`/api/v1/admin/scim-tokens/${encodeURIComponent(tokenId)}/revoke`, { method: "POST", body: "{}" });
  }

  scimServiceProviderConfig(token: string): Promise<ScimServiceProviderConfig> {
    return this.requestScim<ScimServiceProviderConfig>("/scim/v2.0/ServiceProviderConfig", token);
  }

  scimResourceTypes(token: string): Promise<ScimListResponse<Record<string, unknown>>> {
    return this.requestScim<ScimListResponse<Record<string, unknown>>>("/scim/v2.0/ResourceTypes", token);
  }

  scimSchemas(token: string): Promise<ScimListResponse<Record<string, unknown>>> {
    return this.requestScim<ScimListResponse<Record<string, unknown>>>("/scim/v2.0/Schemas", token);
  }

  scimUsers(token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimUser>> {
    const query = new URLSearchParams();
    if (options.startIndex !== undefined) query.set("startIndex", String(options.startIndex));
    if (options.count !== undefined) query.set("count", String(options.count));
    if (options.filter) query.set("filter", options.filter);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.requestScim<ScimListResponse<ScimUser>>(`/scim/v2.0/Users${suffix}`, token);
  }

  scimUser(token: string, userId: string): Promise<ScimUser> {
    return this.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token);
  }

  createScimUser(token: string, input: ScimUserInput): Promise<ScimUser> {
    const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:User"] };
    return this.requestScim<ScimUser>("/scim/v2.0/Users", token, { method: "POST", body: JSON.stringify(body) });
  }

  replaceScimUser(token: string, userId: string, input: ScimUserInput): Promise<ScimUser> {
    const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:User"] };
    return this.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, { method: "PUT", body: JSON.stringify(body) });
  }

  patchScimUser(token: string, userId: string, operations: ScimPatchOperation[]): Promise<ScimUser> {
    return this.requestScim<ScimUser>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, {
      method: "PATCH",
      body: JSON.stringify({ schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"], Operations: operations })
    });
  }

  deleteScimUser(token: string, userId: string): Promise<void> {
    return this.requestScim<void>(`/scim/v2.0/Users/${encodeURIComponent(userId)}`, token, { method: "DELETE" });
  }

  scimGroups(token: string, options: { startIndex?: number; count?: number; filter?: string } = {}): Promise<ScimListResponse<ScimGroup>> {
    const query = new URLSearchParams();
    if (options.startIndex !== undefined) query.set("startIndex", String(options.startIndex));
    if (options.count !== undefined) query.set("count", String(options.count));
    if (options.filter) query.set("filter", options.filter);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.requestScim<ScimListResponse<ScimGroup>>(`/scim/v2.0/Groups${suffix}`, token);
  }

  scimGroup(token: string, groupId: string): Promise<ScimGroup> {
    return this.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token);
  }

  createScimGroup(token: string, input: ScimGroupInput): Promise<ScimGroup> {
    const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:Group"] };
    return this.requestScim<ScimGroup>("/scim/v2.0/Groups", token, { method: "POST", body: JSON.stringify(body) });
  }

  replaceScimGroup(token: string, groupId: string, input: ScimGroupInput): Promise<ScimGroup> {
    const body = { ...input, schemas: input.schemas ?? ["urn:ietf:params:scim:schemas:core:2.0:Group"] };
    return this.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, { method: "PUT", body: JSON.stringify(body) });
  }

  patchScimGroup(token: string, groupId: string, operations: ScimPatchOperation[]): Promise<ScimGroup> {
    return this.requestScim<ScimGroup>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, {
      method: "PATCH",
      body: JSON.stringify({ schemas: ["urn:ietf:params:scim:api:messages:2.0:PatchOp"], Operations: operations })
    });
  }

  deleteScimGroup(token: string, groupId: string): Promise<void> {
    return this.requestScim<void>(`/scim/v2.0/Groups/${encodeURIComponent(groupId)}`, token, { method: "DELETE" });
  }

  webhookEndpoints(): Promise<WebhookEndpoint[]> {
    return this.request<WebhookEndpoint[]>("/api/v1/webhooks/endpoints");
  }

  createWebhookEndpoint(input: { url: string; events?: string[] }): Promise<{ endpoint: WebhookEndpoint; secret: string }> {
    return this.request<{ endpoint: WebhookEndpoint; secret: string }>("/api/v1/webhooks/endpoints", { method: "POST", body: JSON.stringify(input) });
  }

  updateWebhookEndpoint(endpointId: string, input: { url?: string; events?: string[]; status?: "active" | "paused" }): Promise<WebhookEndpoint> {
    return this.request<WebhookEndpoint>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  rotateWebhookEndpoint(endpointId: string): Promise<{ id: string; secret: string }> {
    return this.request<{ id: string; secret: string }>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}/rotate`, { method: "POST", body: "{}" });
  }

  deleteWebhookEndpoint(endpointId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/webhooks/endpoints/${encodeURIComponent(endpointId)}`, { method: "DELETE" });
  }

  webhookDeliveries(): Promise<WebhookDeliverySummary[]> {
    return this.request<WebhookDeliverySummary[]>("/api/v1/webhooks/deliveries");
  }

  supportAccess(): Promise<SupportAccessSession[]> {
    return this.request<SupportAccessSession[]>("/api/v1/admin/support-access");
  }

  requestSupportAccess(input: { reason: string; scopes?: string[]; durationSeconds?: number }): Promise<Partial<SupportAccessSession> & { id: string }> {
    return this.request<Partial<SupportAccessSession> & { id: string }>("/api/v1/support-access/requests", { method: "POST", body: JSON.stringify(input) });
  }

  approveSupportAccess(requestId: string): Promise<SupportAccessGrant> {
    return this.request<SupportAccessGrant>(`/api/v1/support-access/${encodeURIComponent(requestId)}/approve`, { method: "POST", body: "{}" });
  }

  revokeSupportAccess(requestId: string): Promise<{ id: string; status: string }> {
    return this.request<{ id: string; status: string }>(`/api/v1/support-access/${encodeURIComponent(requestId)}/revoke`, { method: "POST", body: "{}" });
  }

  backups(): Promise<BackupRun[]> {
    return this.request<BackupRun[]>("/api/v1/admin/backups");
  }

  requestBackup(kind: "manual" | "restore_test" = "manual"): Promise<Pick<BackupRun, "id" | "kind" | "status">> {
    return this.request<Pick<BackupRun, "id" | "kind" | "status">>("/api/v1/admin/backups", { method: "POST", body: JSON.stringify({ kind }) });
  }

  operationalHealth(): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>("/api/v1/ops/health");
  }

  securityEvents(options: { limit?: number; type?: string; severity?: "info" | "warning" | "critical" } = {}): Promise<SecurityEvent[]> {
    const query = new URLSearchParams();
    if (options.limit !== undefined) query.set("limit", String(options.limit));
    if (options.type) query.set("type", options.type);
    if (options.severity) query.set("severity", options.severity);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request<SecurityEvent[]>(`/api/v1/admin/security-events${suffix}`);
  }

  auditIntegrity(): Promise<{ valid: boolean; checked: number; legacyUnsealed: number; invalid: Array<{ id: string; reason: string }>; headHash: string | null; checkedAt: string }> {
    return this.request<{ valid: boolean; checked: number; legacyUnsealed: number; invalid: Array<{ id: string; reason: string }>; headHash: string | null; checkedAt: string }>("/api/v1/admin/audit/verify");
  }

  sendTrackingEvent(provider: "analytics" | "pixel", input: { eventName: string; eventId?: string; clientId?: string; timestamp?: string; sourceUrl?: string; valueCents?: number; currency?: string; userEmail?: string; customData?: Record<string, string | number | boolean>; idempotencyKey: string }): Promise<{ provider: string; accepted: boolean; eventId: string | null; providerEventId: string | null; sentAt: string }> {
    return this.request<{ provider: string; accepted: boolean; eventId: string | null; providerEventId: string | null; sentAt: string }>(`/api/v1/integrations/${provider}/events`, { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  audit(): Promise<unknown[]> {
    return this.request<unknown[]>("/api/v1/audit");
  }

  cancelBooking(bookingId: string): Promise<{ id: string; status: "cancelled"; refundId?: string | null; refundEligible?: boolean }> {
    return this.request<{ id: string; status: "cancelled"; refundId?: string | null; refundEligible?: boolean }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/cancel`, { method: "POST", body: "{}" });
  }

  applyCoupon(bookingId: string, code: string): Promise<{ bookingId: string; couponId: string; discountCents: number; totalCents?: number }> {
    return this.request<{ bookingId: string; couponId: string; discountCents: number; totalCents?: number }>(`/api/v1/bookings/${encodeURIComponent(bookingId)}/coupon`, { method: "POST", body: JSON.stringify({ code }) });
  }

  validateCoupon(code: string, subtotalCents: number, currency = "EUR", scope?: { entityType: CouponTarget["entityType"]; entityIds: string[] }): Promise<{ code: string; discountCents: number; totalCents: number; currency: string; targets?: CouponTarget[] }> {
    return this.request<{ code: string; discountCents: number; totalCents: number; currency: string; targets?: CouponTarget[] }>("/api/v1/coupons/validate", { method: "POST", body: JSON.stringify({ code, subtotalCents, currency, ...(scope ?? {}) }) });
  }

  coupons(): Promise<Coupon[]> {
    return this.request<Coupon[]>("/api/v1/coupons");
  }

  createCoupon(input: { code: string; kind: "percentage" | "fixed"; amount: number; currency?: string; maxRedemptions?: number; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] }): Promise<{ id: string; code: string; kind: string; amount: number; targets: CouponTarget[] }> {
    return this.request<{ id: string; code: string; kind: string; amount: number; targets: CouponTarget[] }>("/api/v1/coupons", { method: "POST", body: JSON.stringify(input) });
  }

  coupon(couponId: string): Promise<CouponDetails> {
    return this.request<CouponDetails>(`/api/v1/coupons/${encodeURIComponent(couponId)}`);
  }

  updateCoupon(couponId: string, input: { code?: string; kind?: "percentage" | "fixed"; amount?: number; currency?: string | null; maxRedemptions?: number | null; validFrom?: string | null; validUntil?: string | null; status?: "active" | "inactive"; targets?: CouponTarget[] | null }): Promise<CouponDetails> {
    return this.request<CouponDetails>(`/api/v1/coupons/${encodeURIComponent(couponId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  updateCouponStatus(couponId: string, status: "active" | "inactive"): Promise<{ id: string; status: "active" | "inactive" }> {
    return this.request<{ id: string; status: "active" | "inactive" }>(`/api/v1/coupons/${encodeURIComponent(couponId)}/status`, { method: "POST", body: JSON.stringify({ status }) });
  }

  deleteCoupon(couponId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/coupons/${encodeURIComponent(couponId)}`, { method: "DELETE" });
  }

  couponRedemptions(couponId: string, filters: { status?: string; from?: string; to?: string } = {}): Promise<CouponRedemption[]> {
    const query = new URLSearchParams();
    if (filters.status) query.set("status", filters.status);
    if (filters.from) query.set("from", filters.from);
    if (filters.to) query.set("to", filters.to);
    return this.request<CouponRedemption[]>(`/api/v1/coupons/${encodeURIComponent(couponId)}/redemptions${query.size ? `?${query}` : ""}`);
  }

  refundPayment(input: { paymentId: string; amountCents: number; idempotencyKey: string; reason?: string; method?: "mercadopago" | "cash"; occurrenceIds?: string[]; overrideReason?: string }): Promise<RefundSummary> {
    return this.request<RefundSummary>("/api/v1/payments/refund", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  events(): Promise<EventSummary[]> {
    return this.request<EventSummary[]>("/api/v1/events");
  }

  createEvent(input: { name: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"]; categoryId?: string }): Promise<Pick<EventSummary, "id" | "name" | "slug" | "status">> {
    return this.request<Pick<EventSummary, "id" | "name" | "slug" | "status">>("/api/v1/events", { method: "POST", body: JSON.stringify(input) });
  }

  event(eventId: string): Promise<EventSummary> {
    return this.request<EventSummary>(`/api/v1/events/${encodeURIComponent(eventId)}`);
  }

  eventProviders(eventId: string): Promise<Array<{ id: string; displayName: string; status: string }>> {
    return this.request<Array<{ id: string; displayName: string; status: string }>>(`/api/v1/events/${encodeURIComponent(eventId)}/providers`);
  }

  saveEventProviders(eventId: string, providerIds: string[]): Promise<Array<{ id: string; displayName: string; status: string }>> {
    return this.request<Array<{ id: string; displayName: string; status: string }>>(`/api/v1/events/${encodeURIComponent(eventId)}/providers`, { method: "PUT", body: JSON.stringify({ providerIds }) });
  }

  updateEvent(eventId: string, input: { name?: string; slug?: string; description?: string; timezone?: string; status?: EventSummary["status"] }): Promise<EventSummary> {
    return this.request<EventSummary>(`/api/v1/events/${encodeURIComponent(eventId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteEvent(eventId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return this.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/events/${encodeURIComponent(eventId)}`, { method: "DELETE" });
  }

  cancelEvent(eventId: string): Promise<{ id: string; status: "cancelled"; cancelledAttendees: number }> {
    return this.request<{ id: string; status: "cancelled"; cancelledAttendees: number }>(`/api/v1/events/${encodeURIComponent(eventId)}/cancel`, { method: "POST", body: "{}" });
  }

  eventPeriods(eventId: string): Promise<EventPeriod[]> {
    return this.request<EventPeriod[]>(`/api/v1/events/${encodeURIComponent(eventId)}/periods`);
  }

  quoteEvent(input: EventQuoteRequest): Promise<PriceQuote> {
    return this.request<PriceQuote>("/api/v1/events/quote", { method: "POST", body: JSON.stringify(input), headers: input.idempotencyKey ? { "Idempotency-Key": input.idempotencyKey } : undefined });
  }

  createEventCheckout(input: EventCheckoutRequest): Promise<EventCheckout> {
    return this.request<EventCheckout>("/api/v1/events/checkout", { method: "POST", body: JSON.stringify(input), headers: { "Idempotency-Key": input.idempotencyKey } });
  }

  eventOrder(orderId: string): Promise<EventCheckout["order"]> {
    return this.request<EventCheckout["order"]>(`/api/v1/events/orders/${encodeURIComponent(orderId)}`);
  }

  createEventPeriod(input: { eventId: string; startsAt: string; endsAt: string; locationId?: string; capacity: number }): Promise<EventPeriod> {
    return this.request<EventPeriod>("/api/v1/events/periods", { method: "POST", body: JSON.stringify(input) });
  }

  updateEventPeriod(periodId: string, input: { startsAt?: string; endsAt?: string; locationId?: string | null; capacity?: number; status?: "open" | "closed" | "cancelled" }): Promise<EventPeriod & { cancelledAttendees?: number }> {
    return this.request<EventPeriod & { cancelledAttendees?: number }>(`/api/v1/events/periods/${encodeURIComponent(periodId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteEventPeriod(periodId: string): Promise<{ id: string; eventId: string; deleted: boolean }> {
    return this.request<{ id: string; eventId: string; deleted: boolean }>(`/api/v1/events/periods/${encodeURIComponent(periodId)}`, { method: "DELETE" });
  }

  eventTickets(eventId: string): Promise<EventTicket[]> {
    return this.request<EventTicket[]>(`/api/v1/events/${encodeURIComponent(eventId)}/tickets`);
  }

  createEventTicket(input: { eventId: string; name: string; description?: string; priceCents?: number; capacity?: number }): Promise<{ id: string; eventId: string; name: string }> {
    return this.request<{ id: string; eventId: string; name: string }>("/api/v1/events/tickets", { method: "POST", body: JSON.stringify(input) });
  }

  updateEventTicket(ticketId: string, input: { name?: string; description?: string | null; priceCents?: number; capacity?: number; status?: "active" | "inactive" }): Promise<{ id: string; eventId: string; name: string; description?: string | null; priceCents: number; capacity: number; status: "active" | "inactive" }> {
    return this.request<{ id: string; eventId: string; name: string; description?: string | null; priceCents: number; capacity: number; status: "active" | "inactive" }>(`/api/v1/events/tickets/${encodeURIComponent(ticketId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteEventTicket(ticketId: string): Promise<{ id: string; eventId: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; eventId: string; status: "inactive"; deleted: boolean }>(`/api/v1/events/tickets/${encodeURIComponent(ticketId)}`, { method: "DELETE" });
  }

  createEventAttendee(input: { periodId: string; ticketId?: string; bookingId?: string; name: string; email?: string; phone?: string; idempotencyKey?: string }): Promise<EventAttendee & { qrToken?: string }> {
    return this.request<EventAttendee & { qrToken?: string }>("/api/v1/events/attendees", { method: "POST", body: JSON.stringify(input) });
  }

  eventAttendees(periodId: string): Promise<EventAttendee[]> {
    return this.request<EventAttendee[]>(`/api/v1/events/periods/${encodeURIComponent(periodId)}/attendees`);
  }

  checkInEventAttendee(qrToken: string): Promise<{ id: string; periodId: string; name: string; status: string }> {
    return this.request<{ id: string; periodId: string; name: string; status: string }>("/api/v1/events/attendees/check-in", { method: "POST", body: JSON.stringify({ qrToken }) });
  }

  cancelEventAttendee(attendeeId: string): Promise<{ id: string; status: "cancelled" }> {
    return this.request<{ id: string; status: "cancelled" }>(`/api/v1/events/attendees/${encodeURIComponent(attendeeId)}/cancel`, { method: "POST", body: "{}" });
  }

  extras(serviceId?: string, options: { signal?: AbortSignal; includeInactive?: boolean; status?: CatalogExtra["status"] } = {}): Promise<CatalogExtra[]> {
    const query = new URLSearchParams();
    if (serviceId) query.set("serviceId", serviceId);
    if (options.includeInactive) query.set("includeInactive", "true");
    if (options.status) query.set("status", options.status);
    return this.request<CatalogExtra[]>(`/api/v1/catalog/extras${query.size ? `?${query}` : ""}`, { signal: options.signal });
  }

  createExtra(input: { serviceId: string; name: string; description?: string; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: "active" | "inactive" }): Promise<{ id: string; serviceId: string; name: string }> {
    return this.request<{ id: string; serviceId: string; name: string }>("/api/v1/catalog/extras", { method: "POST", body: JSON.stringify(input) });
  }

  extra(extraId: string): Promise<CatalogExtra> {
    return this.request<CatalogExtra>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`);
  }

  updateExtra(extraId: string, input: { serviceId?: string; name?: string; description?: string | null; priceCents?: number; durationMinutes?: number; maxQuantity?: number; status?: CatalogExtra["status"] }): Promise<CatalogExtra> {
    return this.request<CatalogExtra>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteExtra(extraId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/extras/${encodeURIComponent(extraId)}`, { method: "DELETE" });
  }

  packages(options: { includeArchived?: boolean; status?: CatalogPackage["status"] } = {}): Promise<CatalogPackage[]> {
    const query = new URLSearchParams();
    if (options.includeArchived) query.set("includeArchived", "true");
    if (options.status) query.set("status", options.status);
    return this.request<CatalogPackage[]>(`/api/v1/catalog/packages${query.size ? `?${query}` : ""}`);
  }

  createPackage(input: { name: string; slug?: string; description?: string; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string; validUntil?: string }): Promise<Pick<CatalogPackage, "id" | "name" | "slug">> {
    return this.request<Pick<CatalogPackage, "id" | "name" | "slug">>("/api/v1/catalog/packages", { method: "POST", body: JSON.stringify(input) });
  }

  getPackage(packageId: string): Promise<CatalogPackage> {
    return this.request<CatalogPackage>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`);
  }

  updatePackage(packageId: string, input: { name?: string; slug?: string; description?: string | null; priceCents?: number; currency?: string; capacity?: number; status?: CatalogPackage["status"]; validFrom?: string | null; validUntil?: string | null }): Promise<CatalogPackage> {
    return this.request<CatalogPackage>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deletePackage(packageId: string): Promise<{ id: string; status: "archived"; deleted: boolean }> {
    return this.request<{ id: string; status: "archived"; deleted: boolean }>(`/api/v1/catalog/packages/${encodeURIComponent(packageId)}`, { method: "DELETE" });
  }

  locations(options: { serviceId?: string; providerId?: string; signal?: AbortSignal } = {}): Promise<CatalogLocation[]> {
    const query = new URLSearchParams();
    if (options.serviceId) query.set("serviceId", options.serviceId);
    if (options.providerId) query.set("providerId", options.providerId);
    return this.request<CatalogLocation[]>(`/api/v1/catalog/locations${query.size ? `?${query.toString()}` : ""}`, { signal: options.signal });
  }

  createLocation(input: LocationInput): Promise<CatalogLocation> {
    return this.request<CatalogLocation>("/api/v1/catalog/locations", { method: "POST", body: JSON.stringify(input) });
  }

  updateLocation(locationId: string, input: LocationPatch): Promise<CatalogLocation> {
    return this.request<CatalogLocation>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  location(locationId: string): Promise<CatalogLocation> {
    return this.request<CatalogLocation>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`);
  }

  deleteLocation(locationId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/locations/${encodeURIComponent(locationId)}`, { method: "DELETE" });
  }

  providers(options: { serviceId?: string; signal?: AbortSignal } = {}): Promise<CatalogProvider[]> {
    const query = new URLSearchParams();
    if (options.serviceId) query.set("serviceId", options.serviceId);
    return this.request<CatalogProvider[]>(`/api/v1/catalog/providers${query.size ? `?${query.toString()}` : ""}`, { signal: options.signal });
  }

  resources(locationId?: string, options: { includeInactive?: boolean; status?: CatalogResource["status"] } = {}): Promise<CatalogResource[]> {
    const query = new URLSearchParams();
    if (locationId) query.set("locationId", locationId);
    if (options.includeInactive) query.set("includeInactive", "true");
    if (options.status) query.set("status", options.status);
    return this.request<CatalogResource[]>(`/api/v1/catalog/resources${query.size ? `?${query}` : ""}`);
  }

  resource(resourceId: string): Promise<CatalogResource> {
    return this.request<CatalogResource>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`);
  }

  updateResource(resourceId: string, input: { locationId?: string | null; name?: string; type?: string; capacity?: number; status?: CatalogResource["status"]; metadata?: Record<string, unknown> }): Promise<CatalogResource> {
    return this.request<CatalogResource>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteResource(resourceId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/catalog/resources/${encodeURIComponent(resourceId)}`, { method: "DELETE" });
  }

  availabilityRules(filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityRule[]> {
    const query = new URLSearchParams();
    if (filters.providerId) query.set("providerId", filters.providerId);
    if (filters.locationId) query.set("locationId", filters.locationId);
    return this.request<AvailabilityRule[]>(`/api/v1/availability/rules${query.size ? `?${query}` : ""}`);
  }

  createAvailabilityRule(input: { providerId?: string; locationId?: string; weekday: number; startTime: string; endTime: string; timezone?: string; validFrom?: string; validUntil?: string }): Promise<AvailabilityRule> {
    return this.request<AvailabilityRule>("/api/v1/availability/rules", { method: "POST", body: JSON.stringify(input) });
  }

  deleteAvailabilityRule(ruleId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/availability/rules/${encodeURIComponent(ruleId)}`, { method: "DELETE" });
  }

  availabilityExceptions(filters: { providerId?: string; locationId?: string } = {}): Promise<AvailabilityException[]> {
    const query = new URLSearchParams();
    if (filters.providerId) query.set("providerId", filters.providerId);
    if (filters.locationId) query.set("locationId", filters.locationId);
    return this.request<AvailabilityException[]>(`/api/v1/availability/exceptions${query.size ? `?${query}` : ""}`);
  }

  createAvailabilityException(input: { providerId?: string; locationId?: string; startsAt: string; endsAt: string; kind?: "blocked" | "open"; reason?: string }): Promise<AvailabilityException> {
    return this.request<AvailabilityException>("/api/v1/availability/exceptions", { method: "POST", body: JSON.stringify(input) });
  }

  deleteAvailabilityException(exceptionId: string): Promise<{ id: string; deleted: boolean }> {
    return this.request<{ id: string; deleted: boolean }>(`/api/v1/availability/exceptions/${encodeURIComponent(exceptionId)}`, { method: "DELETE" });
  }

  waitlist(): Promise<WaitlistEntry[]> {
    return this.request<WaitlistEntry[]>("/api/v1/waitlist");
  }

  offerWaitlistEntry(entryId: string, ttlSeconds?: number): Promise<SignedAction & { status: string }> {
    return this.request<SignedAction & { status: string }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/offer`, { method: "POST", body: JSON.stringify({ ttlSeconds }) });
  }

  acceptWaitlistEntry(entryId: string): Promise<{ id: string; status: "accepted" }> {
    return this.request<{ id: string; status: "accepted" }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/accept`, { method: "POST", body: "{}" });
  }

  cancelWaitlistEntry(entryId: string): Promise<{ id: string; status: "cancelled" }> {
    return this.request<{ id: string; status: "cancelled" }>(`/api/v1/waitlist/${encodeURIComponent(entryId)}/cancel`, { method: "POST", body: "{}" });
  }

  cart(): Promise<ShoppingCart> {
    return this.request<ShoppingCart>("/api/v1/cart");
  }

  addCartItem(input: { serviceId?: string; packageId?: string; eventPeriodId?: string; ticketId?: string; quantity?: number; metadata?: Record<string, unknown> }): Promise<ShoppingCart> {
    return this.request<ShoppingCart>("/api/v1/cart/items", { method: "POST", body: JSON.stringify(input) });
  }

  updateCartItem(itemId: string, quantity: number): Promise<ShoppingCart> {
    return this.request<ShoppingCart>(`/api/v1/cart/items/${encodeURIComponent(itemId)}`, { method: "PATCH", body: JSON.stringify({ quantity }) });
  }

  removeCartItem(itemId: string): Promise<ShoppingCart> {
    return this.request<ShoppingCart>(`/api/v1/cart/items/${encodeURIComponent(itemId)}`, { method: "DELETE" });
  }

  checkoutCart(input: { confirm?: boolean; paymentMethod?: "mercadopago" | "stripe" | "paypal" | "on_site"; returnUrls?: { success?: string; pending?: string; failure?: string }; idempotencyKey?: string } = {}): Promise<CartCheckout> {
    const idempotencyKey = input.idempotencyKey ?? `cart-checkout-${Date.now()}`;
    return this.request<CartCheckout>("/api/v1/cart/checkout", { method: "POST", body: JSON.stringify({ ...input, idempotencyKey }), headers: { "Idempotency-Key": idempotencyKey } });
  }

  joinWaitlist(input: { serviceId?: string; eventPeriodId?: string; email?: string; persons?: number; desiredStartAt?: string; desiredEndAt?: string }): Promise<{ id: string; status: string }> {
    return this.request<{ id: string; status: string }>("/api/v1/waitlist", { method: "POST", body: JSON.stringify(input) });
  }

  requestPrivacyExport(): Promise<{ id: string; kind: "export"; status: string }> {
    return this.request<{ id: string; kind: "export"; status: string }>("/api/v1/privacy/export", { method: "POST", body: "{}" });
  }

  requestPrivacyErasure(): Promise<{ id: string; kind: "erasure"; status: string }> {
    return this.request<{ id: string; kind: "erasure"; status: string }>("/api/v1/privacy/erase", { method: "POST", body: JSON.stringify({ confirm: "ERASE_MY_DATA" }) });
  }

  createAdminExport(input: AdminExportRequest = {}): Promise<{ id: string; format: "json" | "csv" | "zip"; scope: { entities?: string[] }; status: string }> {
    return this.request<{ id: string; format: "json" | "csv" | "zip"; scope: { entities?: string[] }; status: string }>("/api/v1/admin/exports", { method: "POST", body: JSON.stringify(input) });
  }

  adminExports(): Promise<AdminExport[]> {
    return this.request<AdminExport[]>("/api/v1/admin/exports");
  }

  adminExportDownloadUrl(exportId: string): string {
    return `${this.baseUrl}/api/v1/admin/exports/${encodeURIComponent(exportId)}/download`;
  }

  async createAdminImport(file: File): Promise<{ id: string; fileId: string; status: string }> {
    const form = new FormData();
    form.set("file", file);
    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/admin/imports`, { method: "POST", credentials: this.credentials, body: form });
    const payload = (await response.json()) as ApiResponse<{ id: string; fileId: string; status: string }>;
    if (!response.ok || "error" in payload) {
      const error = "error" in payload ? payload.error : { code: "IMPORT_FAILED", message: "No se pudo crear la importación." };
      throw new KurooApiError(error.code, error.message, error.requestId, error.details);
    }
    return payload.data;
  }

  adminImports(): Promise<AdminImport[]> {
    return this.request<AdminImport[]>("/api/v1/admin/imports");
  }

  async uploadFile(file: File): Promise<{ id: string; key: string; sizeBytes: number; contentType: string }> {
    const form = new FormData();
    form.set("file", file);
    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/files`, { method: "POST", credentials: this.credentials, body: form });
    const payload = (await response.json()) as ApiResponse<{ id: string; key: string; sizeBytes: number; contentType: string }>;
    if (!response.ok || "error" in payload) throw new KurooApiError("UPLOAD_FAILED", "No se pudo subir el archivo.");
    return payload.data;
  }

  entityMedia(options: { entityType?: EntityMediaType; entityId?: string; status?: "active" | "inactive"; includeInactive?: boolean } = {}): Promise<EntityMedia[]> {
    const query = new URLSearchParams();
    if (options.entityType) query.set("entityType", options.entityType);
    if (options.entityId) query.set("entityId", options.entityId);
    if (options.status) query.set("status", options.status);
    if (options.includeInactive) query.set("includeInactive", "true");
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return this.request<EntityMedia[]>(`/api/v1/admin/media${suffix}`);
  }

  async createEntityMedia(input: { entityType: EntityMediaType; entityId: string; file?: File; sourceUrl?: string; position?: number; altText?: string }): Promise<EntityMedia> {
    const form = new FormData();
    form.set("entityType", input.entityType);
    form.set("entityId", input.entityId);
    if (input.file) form.set("file", input.file);
    if (input.sourceUrl) form.set("sourceUrl", input.sourceUrl);
    if (input.position !== undefined) form.set("position", String(input.position));
    if (input.altText !== undefined) form.set("altText", input.altText);
    const response = await this.fetchImpl(`${this.baseUrl}/api/v1/admin/media`, { method: "POST", credentials: this.credentials, body: form });
    const payload = (await response.json()) as ApiResponse<EntityMedia>;
    if (!response.ok || "error" in payload) {
      const error = "error" in payload ? payload.error : { code: "MEDIA_CREATE_FAILED", message: "No se pudo crear el recurso multimedia." };
      throw new KurooApiError(error.code, error.message, error.requestId, error.details);
    }
    return payload.data;
  }

  updateEntityMedia(mediaId: string, input: { position?: number; altText?: string | null; status?: "active" | "inactive" }): Promise<EntityMedia> {
    return this.request<EntityMedia>(`/api/v1/admin/media/${encodeURIComponent(mediaId)}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  deleteEntityMedia(mediaId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
    return this.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/admin/media/${encodeURIComponent(mediaId)}`, { method: "DELETE" });
  }
}
